import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUnifiedApi } from '../../shared/useUnifiedApi';
import {
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  Trash2,
  RefreshCw,
  ArrowLeft,
  Shield,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Truck,
  Container,
  Search,
  X,
  Gauge,
  XCircle,
  AlertCircle,
  FileCheck,
  FileDown,
  History,
  Edit
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import VehicleHistoryModal from '../../components/VehicleHistoryModal';

const STATUS_BADGES = {
  C: { label: 'Conforme', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  Min: { label: 'Mineure', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  Maj: { label: 'Majeure', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  NA: { label: 'N/A', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
};

const ALERT_SEVERITY = {
  overdue: { label: 'En retard', icon: XCircle, color: 'bg-red-500/10 border-red-500/40 text-red-400' },
  urgent: { label: 'Urgent', icon: AlertTriangle, color: 'bg-amber-500/10 border-amber-500/40 text-amber-400' },
  upcoming: { label: 'À venir', icon: Clock, color: 'bg-blue-500/10 border-blue-500/40 text-blue-400' }
};

const MaintenancePage = ({ token, mode = 'admin', workspaceOrgId = null }) => {
  const { get, post, put, del } = useUnifiedApi(token, mode);
  const isMechanic = mode === 'mechanic';
  const wsParam = isMechanic && workspaceOrgId ? `workspace_org_id=${workspaceOrgId}` : '';
  const [searchParams] = useSearchParams();
  const vehicleParam = searchParams.get('vehicle');
  const [activeTab, setActiveTab] = useState(vehicleParam ? 'custom' : 'saaq');
  const [saaqCategories, setSaaqCategories] = useState([]);
  const [saaqAlerts, setSaaqAlerts] = useState([]);
  const [saaqInspections, setSaaqInspections] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customItems, setCustomItems] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleType, setVehicleType] = useState('truck');
  const [expandedCat, setExpandedCat] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showInitModal, setShowInitModal] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', category: 'other', interval_km: '', interval_days: '', vehicle_ids: [] });
  const [historyVehicle, setHistoryVehicle] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const wsQ = wsParam ? `&${wsParam}` : '';
    const wsFirst = wsParam ? `?${wsParam}` : '';
    const [catRes, alertsRes, inspRes, vehiclesRes, itemsRes, recordsRes] = await Promise.all([
      get(`/saas/saaq/categories?vehicle_type=${vehicleType}`),
      get(`/saaq/alerts${wsFirst}`),
      get(`/saaq/inspections${wsFirst}`),
      get(`/fleet/vehicles${wsFirst}`),
      get(`/maintenance/items${wsFirst}`),
      get(`/maintenance/records${wsFirst}`)
    ]);

    if (catRes.success) setSaaqCategories(catRes.data.categories || []);
    if (alertsRes.success) setSaaqAlerts(alertsRes.data || []);
    if (inspRes.success) setSaaqInspections(inspRes.data || []);
    if (vehiclesRes.success) setVehicles(vehiclesRes.data || []);
    if (itemsRes.success) setCustomItems((itemsRes.data || []).filter(i => !i.is_saaq));
    if (recordsRes.success) setRecords(recordsRes.data || []);
    setLoading(false);
  }, [get, vehicleType, wsParam]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInitialize = async () => {
    setInitLoading(true);
    const result = await post('/saaq/initialize', {});
    if (result.success) {
      toast.success(result.data.message);
      setShowInitModal(false);
      fetchData();
    } else {
      toast.error(result.error);
    }
    setInitLoading(false);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    const result = await post('/maintenance/items', {
      name: newItem.name,
      category: newItem.category,
      interval_km: newItem.interval_km ? parseInt(newItem.interval_km) : null,
      interval_days: newItem.interval_days ? parseInt(newItem.interval_days) : null,
      vehicle_ids: newItem.vehicle_ids || []
    });
    if (result.success) {
      toast.success('Item ajoute');
      setShowAddItem(false);
      setNewItem({ name: '', category: 'other', interval_km: '', interval_days: '', vehicle_ids: [] });
      fetchData();
    } else {
      toast.error(result.error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Supprimer cet item d'entretien?")) return;
    const result = await del(`/maintenance/items/${itemId}`);
    if (result.success) {
      toast.success('Item supprime');
      fetchData();
    } else {
      toast.error(result.error);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({
      id: item.id,
      name: item.name,
      category: item.category || 'other',
      interval_km: item.interval_km || '',
      interval_days: item.interval_days || '',
      vehicle_ids: item.vehicle_ids || []
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const result = await put(`/maintenance/items/${editingItem.id}`, {
      name: editingItem.name,
      category: editingItem.category,
      interval_km: editingItem.interval_km ? parseInt(editingItem.interval_km) : null,
      interval_days: editingItem.interval_days ? parseInt(editingItem.interval_days) : null,
      vehicle_ids: editingItem.vehicle_ids || []
    });
    if (result.success) {
      toast.success('Item mis a jour');
      setEditingItem(null);
      fetchData();
    } else {
      toast.error(result.error);
    }
  };

  const handleDownloadPdf = async (inspectionId) => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${apiUrl}/api/saas/saaq/inspections/${inspectionId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur de téléchargement');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Inspection_Mecanique_${inspectionId.substring(0, 8)}.pdf`;
      const disposition = response.headers.get('Content-Disposition');
      if (disposition) {
        const match = disposition.match(/filename="(.+)"/);
        if (match) a.download = match[1];
      }
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const customCategories = {
    engine: { label: 'Moteur', color: 'text-blue-500' },
    tires: { label: 'Pneus', color: 'text-amber-500' },
    brakes: { label: 'Freins', color: 'text-red-500' },
    inspection: { label: 'Inspection', color: 'text-green-500' },
    other: { label: 'Autre', color: 'text-slate-400' }
  };

  const tabs = [
    { id: 'saaq', label: 'SAAQ', icon: Shield, badge: saaqAlerts.length > 0 ? saaqAlerts.length : null },
    { id: 'inspections', label: 'Inspections', icon: ClipboardCheck, badge: saaqInspections.length > 0 ? saaqInspections.length : null },
    { id: 'custom', label: 'Personnalisé', icon: Wrench },
    { id: 'history', label: 'Historique', icon: Clock }
  ];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors">
        <ArrowLeft size={18} />
        <span>Retour au tableau de bord</span>
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="maintenance-page-title">
            <Wrench className="text-amber-500" />
            Entretiens
          </h1>
          <p className="text-slate-400">
            Suivi mécanique et planification des entretiens véhicules
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/admin/fleet">
            <Button variant="outline" className="border-slate-700 text-slate-300 hover:text-white">
              <Truck size={16} className="mr-2" /> Flotte
            </Button>
          </Link>
          <Button onClick={fetchData} variant="outline" className="border-slate-700 text-slate-300">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-amber-500 text-black'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.badge && (
              <span className={`px-1.5 py-0.5 rounded text-xs ${activeTab === tab.id ? 'bg-black/20' : 'bg-red-500 text-white'}`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-slate-900 rounded-xl h-24 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* ===== SAAQ TAB ===== */}
          {activeTab === 'saaq' && (
            <div className="space-y-6">
              {/* SAAQ Alerts */}
              {saaqAlerts.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <AlertTriangle className="text-amber-500" size={20} />
                    Alertes d'inspection
                  </h2>
                  {saaqAlerts.map((alert, idx) => {
                    const sev = ALERT_SEVERITY[alert.severity] || ALERT_SEVERITY.upcoming;
                    const SevIcon = sev.icon;
                    return (
                      <div key={idx} className={`border rounded-xl p-4 ${sev.color}`} data-testid={`saaq-alert-${idx}`}>
                        <div className="flex items-center gap-3">
                          <SevIcon size={20} />
                          <div className="flex-1">
                            <p className="font-semibold">{alert.vehicle_unit}
                              <span className="font-normal text-sm ml-2">
                                ({alert.vehicle_type === 'truck' ? 'Camion' : 'Remorque'})
                              </span>
                            </p>
                            <p className="text-sm opacity-80">{alert.message}</p>
                            {alert.requires_brake_measurement && (
                              <p className="text-xs mt-1 flex items-center gap-1">
                                <Gauge size={12} /> Mesures de freins obligatoires (PNBV &ge; 7 258 kg)
                              </p>
                            )}
                          </div>
                          {alert.last_inspection_date && (
                            <div className="text-right text-xs">
                              <p className="opacity-60">Dernière inspection</p>
                              <p>{new Date(alert.last_inspection_date).toLocaleDateString('fr-CA')}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {saaqAlerts.length === 0 && vehicles.length > 0 && (
                <div className="text-center py-6 bg-green-500/5 border border-green-500/20 rounded-xl">
                  <CheckCircle className="text-green-500 mx-auto mb-2" size={32} />
                  <p className="text-green-400 font-medium">Toutes les inspections sont à jour</p>
                </div>
              )}

              {/* Vehicle type toggle */}
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-white">Catégories d'inspection</h2>
                <div className="flex gap-1 bg-slate-800 rounded-lg p-0.5">
                  <button
                    onClick={() => setVehicleType('truck')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      vehicleType === 'truck' ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white'
                    }`}
                    data-testid="saaq-type-truck"
                  >
                    <Truck size={14} /> Camion
                  </button>
                  <button
                    onClick={() => setVehicleType('trailer')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      vehicleType === 'trailer' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                    data-testid="saaq-type-trailer"
                  >
                    <Container size={14} /> Remorque
                  </button>
                </div>
              </div>

              {/* Info banner */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-start gap-3">
                <Shield className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-slate-400">
                  <p className="text-white font-medium mb-1">Programme d'entretien préventif (PEP)</p>
                  <p>Entretien obligatoire tous les <strong className="text-amber-400">6 mois</strong> pour les véhicules de 4 500 kg+ (PNBV).</p>
                  <p>Mesures de freins obligatoires pour les véhicules de <strong className="text-amber-400">7 258 kg+</strong> (PNBV).</p>
                  <p className="mt-1 text-slate-500">Statuts: <span className="text-green-400">C</span> = Conforme · <span className="text-amber-400">Min</span> = Défectuosité mineure · <span className="text-red-400">Maj</span> = Défectuosité majeure</p>
                </div>
              </div>

              {/* SAAQ Categories accordion */}
              <div className="space-y-2">
                {saaqCategories.map((cat, catIdx) => {
                  const isOpen = expandedCat === cat.code;
                  const measurementCount = cat.items.filter(i => i.requires_measurement).length;
                  return (
                    <div key={cat.code} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid={`saaq-category-${cat.code}`}>
                      <button
                        onClick={() => setExpandedCat(isOpen ? null : cat.code)}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center text-sm font-bold">
                            {cat.order}
                          </div>
                          <div>
                            <p className="text-white font-medium">{cat.name}</p>
                            <p className="text-slate-500 text-xs">
                              {cat.items.length} éléments à vérifier
                              {measurementCount > 0 && ` · ${measurementCount} mesure(s)`}
                            </p>
                          </div>
                        </div>
                        {isOpen ? <ChevronUp className="text-slate-400" size={18} /> : <ChevronDown className="text-slate-400" size={18} />}
                      </button>

                      {isOpen && (
                        <div className="border-t border-slate-800 px-4 pb-4">
                          <table className="w-full text-sm mt-3">
                            <thead>
                              <tr className="text-slate-500 text-xs">
                                <th className="text-left pb-2 font-medium">Code</th>
                                <th className="text-left pb-2 font-medium">Élément</th>
                                <th className="text-center pb-2 font-medium">Loc.</th>
                                {cat.items.some(i => i.requires_measurement) && (
                                  <th className="text-center pb-2 font-medium">Mesure</th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50">
                              {cat.items.map(item => (
                                <tr key={item.code} className="text-slate-300 hover:bg-slate-800/30">
                                  <td className="py-2 text-slate-500 font-mono text-xs">{item.code}</td>
                                  <td className="py-2">
                                    {item.name}
                                    {item.requires_measurement && (
                                      <span className="ml-2 text-xs bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded">
                                        <Gauge size={10} className="inline mr-1" />{item.measurement_unit}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-2 text-center text-xs text-slate-500">{item.loc}</td>
                                  {cat.items.some(i => i.requires_measurement) && (
                                    <td className="py-2 text-center">
                                      {item.requires_measurement && (
                                        <span className="text-amber-400 text-xs">{item.measurement_unit}</span>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Initialize button */}
              <div className="bg-slate-900 border border-dashed border-slate-700 rounded-xl p-6 text-center">
                <FileCheck className="text-slate-600 mx-auto mb-3" size={36} />
                <p className="text-white font-medium mb-1">Initialiser les items d'inspection</p>
                <p className="text-slate-400 text-sm mb-4">
                  Crée automatiquement tous les items d'entretien dans votre système pour un suivi complet.
                </p>
                <Button
                  onClick={() => setShowInitModal(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="init-saaq-btn"
                >
                  <Shield size={16} className="mr-2" /> Initialiser les items d'inspection
                </Button>
              </div>
            </div>
          )}

          {/* ===== INSPECTIONS TAB ===== */}
          {activeTab === 'inspections' && (
            <div className="space-y-4">
              {saaqInspections.length === 0 ? (
                <div className="text-center py-12 bg-slate-900 rounded-xl border border-slate-800">
                  <ClipboardCheck className="text-slate-600 mx-auto mb-4" size={48} />
                  <p className="text-slate-400 font-medium">Aucune inspection mécanique enregistrée</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Les inspections seront disponibles depuis la page Flotte de chaque véhicule
                  </p>
                </div>
              ) : (
                saaqInspections.map(insp => {
                  const statusColors = {
                    passed: 'border-green-500/30 bg-green-500/5',
                    warning: 'border-amber-500/30 bg-amber-500/5',
                    failed: 'border-red-500/30 bg-red-500/5'
                  };
                  const statusLabels = {
                    passed: { text: 'Conforme', color: 'text-green-400', icon: CheckCircle },
                    warning: { text: 'Déf. mineures', color: 'text-amber-400', icon: AlertCircle },
                    failed: { text: 'Déf. majeures', color: 'text-red-400', icon: XCircle }
                  };
                  const st = statusLabels[insp.status] || statusLabels.passed;
                  const StIcon = st.icon;

                  return (
                    <div key={insp.id} className={`border rounded-xl p-4 ${statusColors[insp.status] || ''}`} data-testid={`inspection-${insp.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                            {insp.vehicle_type === 'truck'
                              ? <Truck className="text-amber-500" size={20} />
                              : <Container className="text-blue-400" size={20} />}
                          </div>
                          <div>
                            <p className="text-white font-semibold">{insp.vehicle_unit}</p>
                            <p className="text-slate-400 text-sm">
                              {new Date(insp.inspection_date).toLocaleDateString('fr-CA')}
                              {insp.inspector_name && ` · ${insp.inspector_name}`}
                              {insp.odometer > 0 && ` · ${insp.odometer.toLocaleString()} km`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadPdf(insp.id)}
                            className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-amber-500 transition-colors"
                            title="Télécharger PDF"
                            data-testid={`download-pdf-${insp.id}`}
                          >
                            <FileDown size={18} />
                          </button>
                          <StIcon size={18} className={st.color} />
                          <span className={`text-sm font-medium ${st.color}`}>{st.text}</span>
                        </div>
                      </div>

                      {insp.summary && (
                        <div className="flex gap-4 mt-3 pt-3 border-t border-slate-800/50">
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            <span className="text-slate-400">{insp.summary.conformes} C</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            <span className="text-slate-400">{insp.summary.mineurs} Min</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            <span className="text-slate-400">{insp.summary.majeurs} Maj</span>
                          </div>
                          <div className="text-sm text-slate-500">/ {insp.summary.total} total</div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ===== CUSTOM ITEMS TAB ===== */}
          {activeTab === 'custom' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setShowAddItem(true)} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="add-custom-item-btn">
                  <Plus size={16} className="mr-2" /> Ajouter un item
                </Button>
              </div>
              {customItems.length === 0 ? (
                <div className="text-center py-12 bg-slate-900 rounded-xl border border-slate-800">
                  <Wrench className="text-slate-600 mx-auto mb-4" size={48} />
                  <p className="text-slate-400">Aucun item personnalisé</p>
                  <button onClick={() => setShowAddItem(true)} className="mt-3 text-amber-500 hover:underline text-sm">
                    Ajouter votre premier item
                  </button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {customItems.map(item => (
                    <div key={item.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center ${customCategories[item.category]?.color || 'text-slate-400'}`}>
                            <Wrench size={20} />
                          </div>
                          <div>
                            <p className="text-white font-semibold">{item.name}</p>
                            <p className="text-slate-400 text-sm">{customCategories[item.category]?.label}</p>
                          </div>
                        </div>
                        <button onClick={() => handleDeleteItem(item.id)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`delete-item-${item.id}`}>
                          <Trash2 size={16} />
                        </button>
                        <button onClick={() => handleEditItem(item)} className="text-slate-500 hover:text-amber-400 p-1" data-testid={`edit-item-${item.id}`}>
                          <Edit size={16} />
                        </button>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-800 text-sm text-slate-400">
                        {item.interval_km && <p>Intervalle: {item.interval_km.toLocaleString()} km</p>}
                        {item.interval_days && <p>Intervalle: {item.interval_days} jours</p>}
                        {!item.interval_km && !item.interval_days && <p className="text-slate-500 italic">Aucun intervalle defini</p>}
                        {item.vehicle_ids && item.vehicle_ids.length > 0 ? (
                          <p className="mt-1 flex items-center gap-1 flex-wrap">
                            <Truck size={12} className="text-amber-400" />
                            {item.vehicle_ids.map(vid => {
                              const v = vehicles.find(vh => vh.id === vid);
                              return <span key={vid} className="bg-slate-800 text-xs text-slate-300 px-1.5 py-0.5 rounded">{v ? (v.unit_number || v.plate_number) : vid.slice(0,6)}</span>;
                            })}
                          </p>
                        ) : (
                          <p className="mt-1 text-xs text-slate-500">Tous les vehicules</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== HISTORY TAB ===== */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <p className="text-slate-400 text-sm">Cliquez sur un vehicule pour consulter son historique complet (mesures, bons de travail, inspections).</p>
              {vehicles.length === 0 ? (
                <div className="text-center py-12 bg-slate-900 rounded-xl border border-slate-800">
                  <Clock className="text-slate-600 mx-auto mb-4" size={48} />
                  <p className="text-slate-400">Aucun vehicule dans la flotte</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {vehicles.map(v => (
                    <button
                      key={v.id}
                      onClick={() => setHistoryVehicle(v)}
                      className="bg-slate-900 border border-slate-800 hover:border-amber-500/40 rounded-xl p-4 text-left transition-colors group"
                      data-testid={`history-vehicle-${v.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {v.vehicle_type === 'trailer' ? <Container size={20} className="text-slate-400" /> : <Truck size={20} className="text-amber-400" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm truncate">
                            {v.unit_number ? `#${v.unit_number} ` : ''}{v.plate_number || 'Sans plaque'}
                          </p>
                          <p className="text-slate-500 text-xs">{v.brand} {v.model} {v.year}</p>
                        </div>
                        <History size={16} className="text-slate-600 group-hover:text-amber-400 transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Vehicle History Modal */}
          {historyVehicle && (
            <VehicleHistoryModal
              vehicle={historyVehicle}
              token={token}
              apiPrefix="mechanic/admin/vehicles"
              onClose={() => setHistoryVehicle(null)}
            />
          )}
        </>
      )}

      {/* Initialize SAAQ Modal */}
      {showInitModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl w-full max-w-md border border-slate-700">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Shield className="text-amber-500" size={20} />
                Initialiser les items d'inspection
              </h2>
              <button onClick={() => setShowInitModal(false)} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-slate-400 text-sm">
                Cette action va créer <strong className="text-white">138 items pour camions</strong> et <strong className="text-white">121 items pour remorques</strong> basés sur les formulaires officiels d'inspection mécanique.
              </p>
              <div className="bg-slate-800 rounded-lg p-3 text-sm text-slate-400 space-y-1">
                <p><strong className="text-amber-400">Camions :</strong> 12 catégories (Éclairage, Freins, Pneus, Direction, etc.)</p>
                <p><strong className="text-blue-400">Remorques :</strong> 9 catégories (Éclairage, Attelage, Freins, etc.)</p>
                <p className="text-slate-500 mt-2">Les items déjà existants seront ignorés.</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowInitModal(false)} className="flex-1 border-slate-700">Annuler</Button>
                <Button onClick={handleInitialize} disabled={initLoading} className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" data-testid="confirm-init-saaq-btn">
                  {initLoading ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Shield size={16} className="mr-2" />}
                  Initialiser
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Item Modal */}
      {showAddItem && (
        <ItemFormModal
          title="Ajouter un item personnalise"
          item={newItem}
          setItem={setNewItem}
          vehicles={vehicles}
          customCategories={customCategories}
          onSubmit={handleAddItem}
          onClose={() => setShowAddItem(false)}
          submitLabel="Ajouter"
        />
      )}

      {/* Edit Custom Item Modal */}
      {editingItem && (
        <ItemFormModal
          title="Modifier l'item"
          item={editingItem}
          setItem={setEditingItem}
          vehicles={vehicles}
          customCategories={customCategories}
          onSubmit={handleSaveEdit}
          onClose={() => setEditingItem(null)}
          submitLabel="Enregistrer"
        />
      )}
    </div>
  );
};

function ItemFormModal({ title, item, setItem, vehicles, customCategories, onSubmit, onClose, submitLabel }) {
  const inputClass = "w-full bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-amber-500";
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Nom de l'entretien</label>
            <input
              type="text" value={item.name}
              onChange={(e) => setItem({ ...item, name: e.target.value })}
              className={inputClass}
              placeholder="Ex: Vidange d'huile" required
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Categorie</label>
            <select value={item.category} onChange={(e) => setItem({ ...item, category: e.target.value })}
              className={inputClass}>
              {Object.entries(customCategories).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Intervalle (km)</label>
              <input type="number" value={item.interval_km} onChange={(e) => setItem({ ...item, interval_km: e.target.value })}
                className={inputClass} placeholder="10000" />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Intervalle (jours)</label>
              <input type="number" value={item.interval_days} onChange={(e) => setItem({ ...item, interval_days: e.target.value })}
                className={inputClass} placeholder="365" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Vehicules concernes</label>
            <div className="max-h-32 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg p-2 space-y-1">
              {vehicles.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-2">Aucun vehicule dans la flotte</p>
              ) : (
                <>
                  <label className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-700/50 cursor-pointer">
                    <input type="checkbox"
                      checked={item.vehicle_ids.length === 0}
                      onChange={() => setItem({ ...item, vehicle_ids: [] })}
                      className="accent-amber-500"
                    />
                    <span className="text-xs text-slate-300 font-medium">Tous les vehicules</span>
                  </label>
                  {vehicles.map(v => (
                    <label key={v.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-700/50 cursor-pointer">
                      <input type="checkbox"
                        checked={item.vehicle_ids.includes(v.id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...item.vehicle_ids, v.id]
                            : item.vehicle_ids.filter(id => id !== v.id);
                          setItem({ ...item, vehicle_ids: ids });
                        }}
                        className="accent-amber-500"
                      />
                      <span className="text-xs text-slate-300">{v.unit_number || v.plate_number} — {v.brand} {v.model}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Laissez "Tous" coche pour appliquer a toute la flotte</p>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-slate-700">Annuler</Button>
            <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black">{submitLabel}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MaintenancePage;
